import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    useMemo,
    useCallback,
    useRef
} from 'react';

import api from 'core/services/api';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {

    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [blocked, setBlocked] = useState(false);
    const [blockedReason, setBlockedReason] = useState('');
    // The backend being unreachable (network error, or a 502/503/504 gateway
    // error) is NOT the same thing as an invalid/expired session - it used to
    // be treated identically (session cleared, bounced to /login), silently
    // logging out anyone whose auth check happened to land during a deploy
    // or outage. This is the distinct state for that case: the token is kept,
    // ProtectedRoute shows ServerMaintenance instead of redirecting, and
    // auth is retried automatically once the server is back.
    const [serverDown, setServerDown] = useState(false);

    const logoutRef = useRef(null);

    const userRole = useMemo(() => {
        return user?.role_name || user?.role || null;
    }, [user]);

    // The built-in role this user behaves as for routing / menu / permission
    // gating. For the four system roles this equals userRole; for a custom role
    // (e.g. a "sub super admin") it is the role's base_role sent by the backend.
    const effectiveRole = useMemo(() => {
        const r = user?.base_role || user?.role_name || user?.role || null;
        return r ? String(r).toUpperCase() : null;
    }, [user]);

    const isCustomRole = useMemo(() => Boolean(user?.is_custom_role), [user]);

    const logout = useCallback(() => {
        // Clear React state first — must happen before api.logout()'s async redirect
        // so ProtectedRoute immediately sees unauthenticated state.
        setUser(null);
        sessionStorage.clear();
        // api.logout() is void (not a Promise); handles backend notification + redirect
        api.logout();
    }, []);

    logoutRef.current = logout;

    useEffect(() => {
        if (user?.is_blocked) {
            setBlocked(true);
            setBlockedReason(user.blocked_reason || 'Your account has been blocked');
            const timer = setTimeout(() => {
                if (logoutRef.current) {
                    logoutRef.current();
                }
            }, 3000);
            return () => clearTimeout(timer);
        } else {
            setBlocked(false);
            setBlockedReason('');
        }
    }, [user]);

    // When the axios interceptor calls api.logout() directly (token/refresh expired),
    // it dispatches this event so React clears state immediately — before the async
    // backend logout call and page redirect complete.  Session expiry and password
    // expiry are completely independent flows; this prevents must_change_password
    // state from bleeding into the post-logout redirect.
    useEffect(() => {
        const onForceLogout = () => setUser(null);
        window.addEventListener('auth:force-logout', onForceLogout);
        return () => window.removeEventListener('auth:force-logout', onForceLogout);
    }, []);

    // A response that arrived (even an error one) means the server is up;
    // no response at all, or a gateway-level 502/503/504, means it isn't.
    // Distinct from a 401/403, which means the server IS up and says this
    // token specifically is invalid.
    const isServerUnreachable = (err) =>
        !err?.response || [502, 503, 504].includes(err.response.status);

    const initAuthRef = useRef(null);

    useEffect(() => {
        // Remove legacy password bypass flag — auth state is derived from backend only
        localStorage.removeItem('password_changed_after_reset');

        const initAuth = async () => {

            const token = sessionStorage.getItem('access_token');

            if (!token) {
                setUser(null);
                setServerDown(false);
                setLoading(false);
                return;
            }

            try {
                const res = await api.getCurrentUser();
                setUser(res.data || {});
                setServerDown(false);
            } catch (err) {
                if (isServerUnreachable(err)) {
                    setServerDown(true);
                    // Keep the token and the previous `user` (if any, e.g. a
                    // retry after the server already came back once) - this
                    // is not a logout.
                } else {
                    sessionStorage.clear();
                    setUser(null);
                    setServerDown(false);
                }
            } finally {
                setLoading(false);
            }
        };

        initAuthRef.current = initAuth;
        initAuth();
    }, []);

    // Retries the auth check once the server is confirmed reachable again
    // (see api.js's reconnect polling, which already dispatches this event
    // for the OfflineBanner) - the user never has to manually reload.
    useEffect(() => {
        const onServerOnline = () => {
            if (serverDown && initAuthRef.current) initAuthRef.current();
        };
        window.addEventListener('tas:server:online', onServerOnline);
        return () => window.removeEventListener('tas:server:online', onServerOnline);
    }, [serverDown]);

    // ================= LOGIN =================
    const login = async (username, password) => {

        try {
            const data = await api.login(username, password);

            if (!data || !data.access) {
                return {
                    success: false,
                    message: "Invalid server response"
                };
            }

            // ✅ SAFE USER EXTRACTION
            const userData = data.user || {};

            // ✅ NORMALIZE ROLE
            const role =
                data.role ||
                userData.role_name ||
                userData.role;

            // base_role (effective role) — a custom role like REGIONAL_MANAGER
            // or MANAGER has its own literal `role` but behaves as 'ADMIN' for
            // routing/permission purposes. Without this, callers (e.g. Login.jsx's
            // post-login redirect) only ever see the literal role and misroute
            // custom roles.
            const baseRole = data.base_role || userData.base_role || role;

            // ✅ SET USER — must_change_password comes from backend only
            setUser({
                ...userData,
                role_name: role,
                base_role: baseRole,
            });

            return {
                success: true,
                role,
                base_role: baseRole,
                must_change_password: Boolean(data.must_change_password || userData.must_change_password),
            };

        } catch (err) {
            if (isServerUnreachable(err)) {
                return {
                    success: false,
                    message: 'Server is currently unavailable. Please try again in a moment.',
                    serverDown: true,
                };
            }

            const errorData = err?.response?.data;
            const errorCode = errorData?.code;
            const errorMessage = errorData?.error || errorData?.detail || err?.message;

            if (errorCode === 'DEVICE_PENDING_APPROVAL') {
                return {
                    success: false,
                    message: "Your device is pending approval. Please wait for administrator to approve your device.",
                    code: errorCode,
                    device_pending: true
                };
            }

            if (errorCode === 'DEVICE_REJECTED') {
                return {
                    success: false,
                    message: "Your device has been rejected. Please contact your administrator.",
                    code: errorCode,
                    device_rejected: true
                };
            }

            if (errorCode === 'DEVICE_OWNED_BY_ANOTHER_USER') {
                return {
                    success: false,
                    message: "This device is registered to another user. Please use your own device.",
                    code: errorCode,
                    device_blocked: true
                };
            }

            if (errorCode === 'DEVICE_BLOCKED') {
                return {
                    success: false,
                    message: "This device has been blocked. Please contact your administrator.",
                    code: errorCode,
                    device_blocked: true
                };
            }

            if (errorCode === 'DEVICE_LIMIT_EXCEEDED') {
                return {
                    success: false,
                    message: `Device limit reached. Maximum ${errorData?.max_devices || 5} devices allowed.`,
                    code: errorCode
                };
            }

            if (errorCode === 'RATE_LIMITED') {
                return {
                    success: false,
                    message: errorMessage,
                    code: errorCode
                };
            }

            if (errorCode === 'USER_BLOCKED') {
                return {
                    success: false,
                    message: errorData?.blocked_reason || "Your account has been blocked. Please contact your administrator.",
                    code: errorCode,
                    blocked: true
                };
            }

            return {
                success: false,
                message: errorMessage || "Invalid username or password"
            };
        }
    };

    const refreshUser = useCallback(async () => {
        try {
            const res = await api.getCurrentUser();
            setUser(res.data || {});
        } catch {
            // Axios interceptor handles token refresh; ignore transient errors here
        }
    }, []);

    const value = {
        user,
        setUser,
        loading,
        isAuthenticated: !!user,
        serverDown,
        retryAuth: () => initAuthRef.current && initAuthRef.current(),
        userRole,
        effectiveRole,
        isCustomRole,
        login,
        logout,
        refreshUser,
        blocked,
        blockedReason
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

// ================= HOOK =================
export const useAuth = () => useContext(AuthContext);